import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Database, Key, Link as LinkIcon, Lock, CheckCircle, ArrowRight, Table as TableIcon } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';

export const CityBlueprints = () => {
    const { completeModule } = useAcademyStore();
    const [connections, setConnections] = useState<string[]>([]);
    const [selectedTable, setSelectedTable] = useState<string | null>(null);
    const [feedback, setFeedback] = useState<{ msg: string, type: 'success' | 'error' } | null>(null);

    // Check for completion
    useEffect(() => {
        if (connections.includes('users-posts') && connections.includes('posts-comments')) {
            completeModule('relational-design', 100, 'badge_relational');
        }
    }, [connections, completeModule]);

    const handleTableClick = (id: string) => {
        if (!selectedTable) {
            setSelectedTable(id);
            setFeedback({ msg: `Select a target to connect ${id} to...`, type: 'success' });
        } else {
            if (selectedTable === id) {
                setSelectedTable(null);
                setFeedback(null);
                return;
            }

            // Logic for connections
            let isValid = false;
            let connectionId = '';

            // Users <-> Posts
            if ((selectedTable === 'users' && id === 'posts') || (selectedTable === 'posts' && id === 'users')) {
                connectionId = 'users-posts';
                if (!connections.includes(connectionId)) {
                    setConnections(prev => [...prev, connectionId]);
                    setFeedback({ msg: "✅ Road Built! One User can write MANY Posts. (1:N Relationship)", type: 'success' });
                    isValid = true;
                }
            }
            // Posts <-> Comments
            else if ((selectedTable === 'posts' && id === 'comments') || (selectedTable === 'comments' && id === 'posts')) {
                connectionId = 'posts-comments';
                if (!connections.includes(connectionId)) {
                    setConnections(prev => [...prev, connectionId]);
                    setFeedback({ msg: "✅ Road Built! One Post can have MANY Comments. (1:N Relationship)", type: 'success' });
                    isValid = true;
                }
            } else {
                setFeedback({ msg: "❌ Dead End! These buildings don't need a direct road.", type: 'error' });
            }

            setSelectedTable(null);
            if (isValid) {
                setTimeout(() => setFeedback(null), 2000);
            }
        }
    };

    const isUsageReset = () => {
        setConnections([]);
        setFeedback(null);
        setSelectedTable(null);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 font-sans text-white flex flex-col">
            {/* Header */}
            <div className="bg-slate-900/50 backdrop-blur-md border-b border-white/10 px-8 py-4 flex justify-between items-center sticky top-0 z-50">
                <Link to="/" className="flex items-center gap-2 text-slate-300 font-bold hover:text-cyan-400 transition-colors">
                    <Home size={20} /> Dashboard
                </Link>
                <div className="bg-cyan-500/20 text-cyan-300 px-4 py-2 rounded-full text-sm font-bold border border-cyan-500/30 flex items-center gap-2">
                    <Database size={18} /> Module: Data Architect
                </div>
            </div>

            <main className="flex-1 p-8 flex flex-col items-center justify-start max-w-7xl mx-auto w-full">

                <div className="text-center mb-8">
                    <h1 className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 mb-4 drop-shadow-sm">
                        Relational City 🏙️
                    </h1>
                    <p className="text-slate-400 text-lg max-w-3xl mx-auto mb-6">
                        Welcome to <strong>Schema City</strong>. Your job is to build roads (Relationships) between buildings (Tables).
                        We need data to flow from Users to Posts, and Posts to Comments.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
                        <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700">
                            <div className="text-yellow-400 font-mono font-bold text-sm mb-1">🔑 PK (Primary Key)</div>
                            <div className="text-slate-400 text-xs">The <strong>Street Address</strong>. Every building has a unique one.</div>
                        </div>
                        <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700">
                            <div className="text-cyan-400 font-mono font-bold text-sm mb-1">🔗 FK (Foreign Key)</div>
                            <div className="text-slate-400 text-xs">A <strong>Signpost</strong> pointing to another building's Address.</div>
                        </div>
                        <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700">
                            <div className="text-green-400 font-mono font-bold text-sm mb-1">🛣️ 1:N (One-to-Many)</div>
                            <div className="text-slate-400 text-xs">One User ➡️ Many Posts. One Post ➡️ Many Comments.</div>
                        </div>
                    </div>
                </div>

                {/* GAME AREA */}
                <div className="w-full h-[600px] bg-slate-900/50 rounded-3xl border border-slate-700 shadow-2xl relative overflow-hidden backdrop-blur-sm">

                    {/* Grid Background */}
                    <div className="absolute inset-0 opacity-20"
                        style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(56, 189, 248, 0.3) 1px, transparent 0)', backgroundSize: '24px 24px' }}
                    />

                    {/* Feedback Toast */}
                    <AnimatePresence>
                        {feedback && (
                            <motion.div
                                initial={{ y: -50, opacity: 0, x: '-50%' }}
                                animate={{ y: 0, opacity: 1, x: '-50%' }}
                                exit={{ y: -50, opacity: 0, x: '-50%' }}
                                className={`absolute top-8 left-1/2 z-50 px-6 py-3 rounded-xl font-bold backdrop-blur-md shadow-lg border ${feedback.type === 'success' ? 'bg-green-500/20 text-green-300 border-green-500/50' : 'bg-red-500/20 text-red-300 border-red-500/50'
                                    }`}
                            >
                                {feedback.msg}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* SVG Connections (Underlay) */}
                    <svg className="absolute inset-0 w-full h-full pointer-events-none z-0">
                        {/* Define Gradients */}
                        <defs>
                            <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" style={{ stopColor: '#3b82f6', stopOpacity: 1 }} />
                                <stop offset="100%" style={{ stopColor: '#22c55e', stopOpacity: 1 }} />
                            </linearGradient>
                        </defs>

                        {connections.includes('users-posts') && (
                            <motion.path
                                initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
                                transition={{ duration: 1 }}
                                d="M 250 150 C 400 150, 400 150, 550 150" // Hardcoded curve for demo layout
                                stroke="url(#grad1)" strokeWidth="6" fill="none" strokeDasharray="10,5" strokeLinecap="round"
                            />
                        )}
                        {connections.includes('posts-comments') && (
                            <motion.path
                                initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
                                transition={{ duration: 1 }}
                                d="M 650 250 C 650 350, 650 350, 650 450" // Vertical curve
                                stroke="#a855f7" strokeWidth="6" fill="none" strokeDasharray="10,5" strokeLinecap="round"
                            />
                        )}
                    </svg>

                    {/* Tables Container (Absolute Positioning for Diagram look) */}
                    <div className="absolute inset-0 z-10">

                        {/* 1. USERS TABLE */}
                        <div className="absolute top-[50px] left-[50px] md:left-[100px]">
                            <DbTable
                                title="Users"
                                color="blue"
                                isSelected={selectedTable === 'users'}
                                onClick={() => handleTableClick('users')}
                                fields={[
                                    { name: 'id', type: 'PK', icon: Key },
                                    { name: 'username', type: 'VARCHAR' },
                                    { name: 'email', type: 'VARCHAR' },
                                ]}
                            />
                        </div>

                        {/* 2. POSTS TABLE */}
                        <div className="absolute top-[50px] right-[50px] md:right-[150px]">
                            <DbTable
                                title="Posts"
                                color="green"
                                isSelected={selectedTable === 'posts'}
                                onClick={() => handleTableClick('posts')}
                                fields={[
                                    { name: 'id', type: 'PK', icon: Key },
                                    { name: 'user_id', type: 'FK', icon: LinkIcon, highlight: true },
                                    { name: 'content', type: 'TEXT' },
                                ]}
                            />
                        </div>

                        {/* 3. COMMENTS TABLE */}
                        <div className="absolute bottom-[50px] right-[50px] md:right-[150px]">
                            <DbTable
                                title="Comments"
                                color="purple"
                                isSelected={selectedTable === 'comments'}
                                onClick={() => handleTableClick('comments')}
                                fields={[
                                    { name: 'id', type: 'PK', icon: Key },
                                    { name: 'post_id', type: 'FK', icon: LinkIcon, highlight: true },
                                    { name: 'text', type: 'TEXT' },
                                ]}
                            />
                        </div>

                    </div>

                    {/* Reset Button */}
                    <button
                        onClick={isUsageReset}
                        className="absolute bottom-8 left-8 z-20 text-slate-500 hover:text-white text-sm font-bold flex items-center gap-2 transition-colors"
                    >
                        Reset Diagram
                    </button>

                    {/* Gorilla Architect */}
                    <div className="absolute bottom-0 left-0 w-64 pointer-events-none z-10 hidden md:block opacity-50">
                        <img src="/assets/gorillas/gorilla_city_planner.png" className="w-full" alt="Planner" />
                    </div>

                </div>

                {/* Success State */}
                {connections.length === 2 && (
                    <ModuleSuccess
                        title="Schema Architect"
                        badgeId="badge_relational"
                        onRetry={isUsageReset}
                    >
                        <div className="bg-cyan-900/30 p-6 rounded-2xl border border-cyan-500/30 text-left">
                            <h4 className="text-xl font-bold text-cyan-300 mb-4">🏙️ City Planning Complete!</h4>
                            <div className="space-y-4 text-cyan-100 text-sm">
                                <p>
                                    You've successfully mapped the <strong>Social Network Schema</strong>.
                                </p>
                                <ul className="space-y-2 list-disc list-inside text-cyan-200/80">
                                    <li><strong>Users</strong> are the foundation.</li>
                                    <li><strong>Posts</strong> rely on Users (via <code>user_id</code> FK).</li>
                                    <li><strong>Comments</strong> rely on Posts (via <code>post_id</code> FK).</li>
                                </ul>
                                <p className="italic border-t border-cyan-500/20 pt-4 mt-4">
                                    "Without these Foreign Key roads, the delivery trucks (Queries) wouldn't know where to go!"
                                </p>
                            </div>
                        </div>
                    </ModuleSuccess>
                )}

            </main>
        </div>
    );
};

// Sub-component for Table UI
const DbTable = ({ title, color, fields, isSelected, onClick }: any) => {
    const colorClasses: any = {
        blue: 'border-blue-500/50 shadow-blue-500/20 bg-blue-500/10',
        green: 'border-green-500/50 shadow-green-500/20 bg-green-500/10',
        purple: 'border-purple-500/50 shadow-purple-500/20 bg-purple-500/10'
    };

    const headerColors: any = {
        blue: 'bg-gradient-to-r from-blue-600 to-blue-500',
        green: 'bg-gradient-to-r from-green-600 to-green-500',
        purple: 'bg-gradient-to-r from-purple-600 to-purple-500'
    };

    return (
        <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onClick}
            animate={{
                scale: isSelected ? 1.05 : 1,
                boxShadow: isSelected ? '0 0 30px rgba(56, 189, 248, 0.4)' : '0 10px 30px rgba(0,0,0,0.3)',
                borderColor: isSelected ? 'rgb(56 189 248)' : undefined
            }}
            className={`w-64 rounded-xl overflow-hidden backdrop-blur-xl border-2 cursor-pointer transition-colors ${colorClasses[color] || 'bg-slate-800 border-slate-700'}`}
        >
            <div className={`${headerColors[color]} p-3 flex justify-between items-center`}>
                <span className="font-bold text-white flex items-center gap-2">
                    <TableIcon size={16} /> {title}
                </span>
                {isSelected && <div className="w-2 h-2 bg-white rounded-full animate-pulse" />}
            </div>
            <div className="p-4 space-y-3">
                {fields.map((field: any, i: number) => (
                    <div key={i} className={`flex items-center justify-between text-sm p-2 rounded ${field.highlight ? 'bg-white/10' : ''}`}>
                        <div className="flex items-center gap-2 font-mono text-slate-300">
                            {field.icon && <field.icon size={12} className={field.name === 'id' ? 'text-yellow-400' : 'text-slate-400'} />}
                            <span className={field.highlight ? 'text-white font-bold' : ''}>{field.name}</span>
                        </div>
                        <span className="text-xs text-slate-500 font-bold">{field.type}</span>
                    </div>
                ))}
            </div>
        </motion.div>
    );
};

export default CityBlueprints;
